import { z } from 'zod';
const idPattern = /^[a-z][a-z0-9_]{1,30}$/;
/** Stable addon id, unique within the host app (docs/CONVENTIONS.md §K). */
export const AddonIdSchema = z.string().regex(idPattern, 'addon id: lowercase letters, digits, underscore; 2–31 chars');
const HostAppIdSchema = z.string().regex(idPattern, 'host app id: lowercase letters, digits, underscore; 2–31 chars');
export const AddonNavGroupSchema = z.object({
    /** Sidebar section header. The addon owns this; the host must not invent groups. */
    group: z.string().min(1),
});
export const AddonNavLinkSchema = z.object({
    /** Must be `/<addon-id>` or a subpath (the addon id is the unique URL prefix). */
    to: z.string().min(1),
    label: z.string().min(1),
    /** Lucide icon export name (e.g. `Wallet`). The addon web module maps this to a component. */
    icon: z.string().min(1),
    end: z.boolean().optional(),
    badge: z.string().optional(),
});
/** One sidebar entry: a group header or a link. Written in the addon, not the host. */
export const AddonNavItemSchema = z.union([AddonNavGroupSchema, AddonNavLinkSchema]);
export const AddonPermissionsSchema = z.object({
    /** Roles that may enable/disable the addon. */
    manage: z.array(z.string()).default(['owner']),
    /** Roles that may use addon routes when it is enabled. */
    use: z.array(z.string()).default(['owner', 'manager', 'employee']),
});
/** `os-addon.json` at the root of every addon package. */
export const AddonManifestSchema = z.object({
    $schema: z.string().optional(),
    id: AddonIdSchema,
    name: z.string().min(1),
    description: z.string().default(''),
    /** 1–3 characters shown on the Add-ons management card. */
    icon: z.string().min(1).max(3),
    hostApp: HostAppIdSchema,
    version: z.string().default('0.1.0'),
    nav: z.array(AddonNavItemSchema).default([]),
    permissions: AddonPermissionsSchema.default({}),
});
/** Entry in the host `os-app.json.addons` array. */
export const AppAddonRefSchema = z.object({
    id: AddonIdSchema,
    enabledByDefault: z.boolean().default(true),
});
export function parseAddonManifest(json) {
    return AddonManifestSchema.parse(json);
}
export function assertAddonNav(manifest) {
    const prefix = `/${manifest.id}`;
    for (const item of manifest.nav) {
        if ('group' in item)
            continue;
        if (item.to !== prefix && !item.to.startsWith(`${prefix}/`)) {
            throw new Error(`addon "${manifest.id}" nav path "${item.to}" must be ${prefix} or a subpath`);
        }
    }
}
export function isAddonNavGroup(item) {
    return 'group' in item;
}
//# sourceMappingURL=addon.js.map